import React, { useEffect, useState } from "react";
import "./Provider.css";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import {
  addProvider,
  fetchOrgName,
  getOrganisation,
  getProvidersNames,
  updateOrganisationDetails,
} from "../../../config/LiveApi";
import { toast } from "react-toastify";
import Spinner from "../../Spin/Spinner";
import { useSelector } from "react-redux";
import Select from "react-select";

const AddProviderForm = ({
  formData,
  handleInputChange,
  setFormData,
  initialFormValue,
}) => {
  const FIXED_DATE = "2025-01-01";
  const [selectedValue, setSelectedValue] = useState("payin");
  const [isLoading, setIsLoading] = useState(false);
  const [orgOptions, setOrgOptions] = useState([]);
  const [fallbackProviderOptions, setFallbackProviderOptions] = useState([]);
  const [providerType, setProviderType] = useState("payin");

  const options = [
    { name: "payin", label: "payin" },
    { name: "payout", label: "payout" },
    { name: "both", label: "both" },
  ];
  const modeMap = {
    payin: [
      { label: "UPI INTENT", value: "UPI_INTENT" },
      { label: "UPI Collect", value: "UPI_COLLECT" },
    ],
    payout: [
      { label: "IMPS", value: "IMPS" },
      { label: "UPI", value: "UPI" },
    ],
    both: [{ label: "UPI INTENT", value: "UPI_INTENT" }],
  };
  const token = useSelector((store) => store.userReducer.token);

  useEffect(() => {
    if (selectedValue === "both") {
      setFormData((prev) => ({
        ...prev,
        type: "both",
        mode: "UPI_INTENT",
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        type: selectedValue,
        mode: "",
      }));
    }    
  }, [token, selectedValue]);

  useEffect(() => {
    const fetchOrganisations = async () => {
      try {
        const response = await getOrganisation(token);
        const orgData = response.data;
        setOrgOptions(orgData);
        const providerRes = await getProvidersNames(token);
        // console.log("Providers:", providerRes);

        setFallbackProviderOptions(providerRes);
      } catch (err) {
        console.error("Error fetching organisations:", err.message);
        toast.error("Unable to fetch organisations.");
      }
    };
    fetchOrganisations();
  },[]);

  const providerOptions = fallbackProviderOptions.map(p => ({
  value: p.value,
  label: p.orgName + " - " + p.label,
  isActive: p.isActive,
}));

const formatOptionLabel = (option) => (
  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
    <span
      style={{
        width: 8,
        height: 8,
        borderRadius: "50%",
        backgroundColor: option.isActive ? "green" : "red",
      }}
    />
    <span>{option.label}</span>
  </div>
);

  const handleDropdownChange = (e) => {
    const value = e.target.value;

    setSelectedValue(value);
    setProviderType(value);

    if (value === "both") {
      // auto-assign mode = UPI_INTENT
      setFormData((prev) => ({
        ...prev,
        type: "both",
        mode: "UPI_INTENT",
      }));
    } else {
      // clear mode for payin/payout
      setFormData((prev) => ({
        ...prev,
        type: value,
        mode: "",
      }));
    }
  };

  const onSubmit = async (event) => {
    event.preventDefault();

    const type = providerType.toLowerCase();

    // Payin limit check
    if (providerType === "payin" || providerType === "both") {
      const min = Number(formData.payinMinPerTxn);
      const max = Number(formData.payinMaxPerTxn);

      if (min && max && max <= min) {
        toast.error("Maximum Payin must be greater than Minimum Payin.");
        return;
      }
    }
    // Payout limit check
    if (providerType === "payout" || providerType === "both") {
      const min = Number(formData.payoutMinPerTxn);
      const max = Number(formData.payoutMaxPerTxn);

      if (min && max && max <= min) {
        toast.error("Maximum Payout must be greater than Minimum Payout.");
        return;
      }
    }

    // Mode validation
    if ((type === "payin" || type === "payout") && !formData.mode) {
      toast.error("Please select a mode.");
      return;
    }

    // Time validation
    // if (formData.startTime && formData.endTime) {
    //   const [sh, sm] = formData.startTime.split(":").map(Number);
    //   const [eh, em] = formData.endTime.split(":").map(Number);

    //   const startMinutes = sh * 60 + sm;
    //   const endMinutes = eh * 60 + em;

    //   if (endMinutes <= startMinutes) {
    //     toast.error("End time must be greater than Start time.");
    //     return;
    //   }
    // }

    // Length validation
    const minL = Number(formData.minLength);
    const maxL = Number(formData.maxLength);

    if (minL && maxL && maxL <= minL) {
      toast.error("Maximum Length must be greater than Minimum Length.");
      return;
    }
    const payload = {
      ...formData,
      validLength:
        formData.minLength === "" && formData.maxLength === ""
          ? ""
          : `${formData.minLength}-${formData.maxLength}`,
      startTime: `${FIXED_DATE}T${formData.startTime}`,
      endTime: `${FIXED_DATE}T${formData.endTime}`,
    };

    setIsLoading(true);
    try {
      await addProvider(token, payload);
      toast.success("Provider added successfully");
      updateOrgDetails();
    } catch (err) {
      console.log(err.message);
      toast.error("Unable to add provider.");
    }

    setFormData(initialFormValue);
    setSelectedValue(initialFormValue.type);
    setProviderType(initialFormValue.type);
    setIsLoading(false);
  };

  const updateOrgDetails = async () => {
    try {
      const response = await fetchOrgName(formData.orgName, token);
      const orgId = response.data[0].id;

      const payload = {
        type: formData.type,
        providerName: formData.providerName,
      };

      await updateOrganisationDetails(orgId, payload, token);
      toast.success("Provider added to the organisation.");
    } catch (err) {
      console.log(err.message);
    }
  };

  return (
    <div className="provider-form">
      {isLoading ? (
        <Spinner />
      ) : (
        <form onSubmit={onSubmit}>
          <p className="label-data">Provider Name</p>
          <input
            type="text"
            name="providerName"
            placeholder="Provider Name"
            value={formData?.providerName}
            onChange={handleInputChange}
          />
          <p className="label-data">Provider Type</p>
          <select
            name="type"
            value={selectedValue}
            onChange={handleDropdownChange}
            style={{ width: "70%", height: "35px", borderRadius: "8px" }}
          >
            {options.map((option) => (
              <option key={option.name} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <p className="label-data">Organization Name</p>
          <select
            style={{ width: "70%", height: "35px", bordeRadius: "8px" }}
            name="orgName"
            value={formData?.orgName || ""}
            onChange={handleInputChange}
          >
            <option value="">Select Organization</option>
            {orgOptions.map((org) => (
              <option key={org.id} value={org.orgName}>
                {org.orgName}
              </option>
            ))}
          </select>

          <p className="label-data">Mode</p>
          <select
            name="mode"
            value={formData.mode}
            disabled={selectedValue === "both"} // auto-lock mode when both
            onChange={handleInputChange}
            style={{ width: "70%", height: "35px", borderRadius: "8px" }}
          >
            <option value="">Select Mode</option>

            {modeMap[selectedValue]?.map((mode) => (
              <option key={mode.value} value={mode.value}>
                {mode.label}
              </option>
            ))}
          </select>
          {selectedValue === "both" && (
            <small style={{ display: "block", color: "#6b7280", marginTop: "4px" }}>
              Mode is fixed to UPI INTENT when Provider Type is both.
            </small>
          )}

          {providerType === "payin" || providerType === "both" ? (
            <>
              <p className="label-data">Payin Provider Commission</p>
              <input
                type="text"
                name="providerPayinComm"
                placeholder="Payin Commission"
                value={formData?.providerPayinComm}
                onChange={handleInputChange}
              />
            </>
          ) : null}

          {providerType === "payout" || providerType === "both" ? (
            <>
              <p className="label-data">Payout Provider Commission</p>
              <input
                type="text"
                name="providerPayoutComm"
                placeholder="Payout Commission"
                value={formData?.providerPayoutComm}
                onChange={handleInputChange}
              />
            </>
          ) : null}

          {providerType === "payin" || providerType === "both" ? (
            <>
              <p className="label-data">Minimum Payin Per Transaction</p>
              <input
                type="text"
                name="payinMinPerTxn"
                placeholder="Minimum Payin Per Transaction"
                value={formData?.payinMinPerTxn}
                onChange={handleInputChange}
              />
            </>
          ) : null}

          {providerType === "payin" || providerType === "both" ? (
            <>
              <p className="label-data">Maximum Payin Per Transaction</p>
              <input
                type="text"
                name="payinMaxPerTxn"
                placeholder="Maximum Payin Per Transaction"
                value={formData?.payinMaxPerTxn}
                onChange={handleInputChange}
              />
            </>
          ) : null}

          {providerType === "payout" || providerType === "both" ? (
            <>
              <p className="label-data">Minimum Payout Per Transaction</p>
              <input
                type="text"
                name="payoutMinPerTxn"
                placeholder="Minimum Payout Per Transaction"
                value={formData?.payoutMinPerTxn}
                onChange={handleInputChange}
              />
            </>
          ) : null}

          {providerType === "payout" || providerType === "both" ? (
            <>
              <p className="label-data">Maximum Payout Per Transaction</p>
              <input
                type="text"
                name="payoutMaxPerTxn"
                placeholder="Maximum Payout Per Transaction"
                value={formData?.payoutMaxPerTxn}
                onChange={handleInputChange}
              />
            </>
          ) : null}

          {providerType === "payin" || providerType === "both" ? (
            <>
              <p className="label-data">Total Payin Limit Per Day</p>
              <input
                type="text"
                name="totalPayinLimit"
                placeholder="Total Payin Limit Per Day"
                value={formData?.totalPayinLimit}
                onChange={handleInputChange}
              />
            </>
          ) : null}

          {providerType === "payout" || providerType === "both" ? (
            <>
              <p className="label-data">Total Payout Limit Per Day</p>
              <input
                type="text"
                name="totalPayoutLimit"
                placeholder="Total Payout Limit Per Day"
                value={formData?.totalPayoutLimit}
                onChange={handleInputChange}
              />
            </>
          ) : null}

          {/* {providerType === "payin" || providerType === "both" ? (
            <>
              <p className="label-data">Current Payin Amount</p>
              <input
                type="text"
                name="currentPayinAmount"
                placeholder="Current Payin Amount"
                value={formData?.currentPayinAmount}
                onChange={handleInputChange}
              />
            </>
          ) : null}

          {providerType === "payout" || providerType === "both" ? (
            <>
              <p className="label-data">Current Payout Amount</p>
              <input
                type="text"
                name="currentPayoutAmount"
                placeholder="Current Payout Amount"
                value={formData?.currentPayoutAmount}
                onChange={handleInputChange}
              />
            </>
          ) : null} */}

          <p className="label-data">Start Time</p>
          <input
            type="time"
            name="startTime"
            value={formData?.startTime ? formData.startTime : ""}
            onChange={handleInputChange}
          />

          <p className="label-data">End Time</p>
          <input
            type="time"
            name="endTime"
            value={formData?.endTime ? formData.endTime : ""}
            onChange={handleInputChange}
          />

          <p className="label-data">Valid Length</p>
          <div style={{ display: "flex", gap: "10px" }}>
            <input
              type="number"
              name="minLength"
              placeholder="Min"
              value={formData.minLength || ""}
              onChange={handleInputChange}
              style={{ width: "80px" }}
            />

            <input
              type="number"
              name="maxLength"
              placeholder="Max"
              value={formData.maxLength || ""}
              onChange={handleInputChange}
              style={{ width: "80px" }}
            />
          </div>
          <div>
            <>
              <p className="label-data">Fallback Provider Name</p>
              <Select
                options={providerOptions}
                value={providerOptions.find(
                  o => o.value === formData.fallbackProviderId
                )}
                onChange={(selected) =>
                  setFormData(prev => ({
                    ...prev,
                    fallbackProviderId: selected?.value || "",
                  }))
                }
                formatOptionLabel={formatOptionLabel}
                placeholder="Select Fallback Provider"
              />
            </> 
          </div>

          <FormControl>
            <FormLabel className="label-data">Provider Active</FormLabel>
            <RadioGroup
              name="isActive"
              value={formData?.isActive}
              onChange={handleInputChange}
            >
              <FormControlLabel value="true" control={<Radio />} label="Yes" />
              <FormControlLabel value="false" control={<Radio />} label="No" />
            </RadioGroup>
          </FormControl>

          <div className="modal-buttons">
            <button type="submit">Add</button>
          </div>
        </form>
      )}
    </div>
  );
};

export default AddProviderForm;
